import IGrazePaymentPayload from '../../models/IGrazePaymentPayload';
import IPaymentTransaction from '../../models/IPaymentTransaction';

export type GrazePaymentStatus = 'paid' | 'pending' | 'failed';

const FAILED_STATES = ['failed', 'failure', 'declined', 'decline', 'denied', 'cancelled', 'canceled', 'error', 'void', 'voided', 'refunded', 'refund'];
const PAID_STATES = ['authorize', 'authorized', 'authorised', 'capture', 'captured', 'sale', 'paid', 'success', 'successful', 'succeeded', 'completed', 'settled'];

export const isGrazePaymentPayload = (data: any): data is IGrazePaymentPayload =>
  !!data && typeof data === 'object' &&
  ('idPayment' in data || Array.isArray(data.hPayPaymentTransactions));

export const toNumber = (value: unknown): number => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    return Number(value);
  }
  return NaN;
};

export const getPaidAmount = (payload: IGrazePaymentPayload): number => {
  const cents = toNumber(payload.hPayPaymentTransactions?.[0]?.paymentDetails?.totalAmount);
  return Number.isFinite(cents) ? cents / 100 : NaN;
};

export const getGrazePaymentStatus = (payload: IGrazePaymentPayload): GrazePaymentStatus => {
  const state = (payload.state || payload.hPayPaymentTransactions?.[0]?.state || '').trim().toLowerCase();
  if (FAILED_STATES.includes(state)) {
    return 'failed';
  }
  const amount = getPaidAmount(payload);
  if (!payload.idPayment || !Number.isFinite(amount) || amount <= 0) {
    return 'pending';
  }
  return PAID_STATES.includes(state) ? 'paid' : 'pending';
};

export const toPaymentTransaction = (payload: IGrazePaymentPayload, applySurcharge = false): IPaymentTransaction => {
  if (getGrazePaymentStatus(payload) !== 'paid') {
    throw new Error('Cannot create an order transaction for an unconfirmed payment.');
  }
  // totalTip is often 0 while tipAmount/tip carry the real value.
  // With surcharge enabled the tip arrives in dollars; otherwise it is in cents.
  const tip = [payload.tipAmount, payload.tip, payload.totalTip].map(toNumber).find((value) => value > 0) ?? 0;
  const totalTip = applySurcharge ? tip : tip / 100;
  const totalSurcharge = toNumber(payload.surchargeAmount ?? 0);
  for (const [field, value] of [['tipAmount', totalTip], ['surchargeAmount', totalSurcharge]] as const) {
    if (!Number.isFinite(value) || value < 0) {
      throw new Error(`Payment payload contains an invalid ${field}.`);
    }
  }
  const createdUtc = payload.createdUtc ? new Date(payload.createdUtc) : new Date();
  const status = payload.state || payload.hPayPaymentTransactions?.[0]?.state || '';
  return {
    cCTransactionId: payload.idPayment as string,
    paymentProcessorTransactionId: payload.idPayment as string,
    transactionState: 3,
    transactionType: 'Online',
    paymentMethod: 'Credit Card',
    paymentMethodId: payload.paymentMethodId || undefined,
    cardIssuer: payload.cardIssuer || '',
    maskedCardNumber: payload.maskedCardNumber || '',
    madeBy: '',
    receivedBy: '',
    comment: status,
    cCStatus: status,
    cCPostal: payload.zipCode || undefined,
    totalPaid: getPaidAmount(payload),
    totalTip,
    totalRefund: 0,
    totalSurcharge,
    dateApplied: Number.isFinite(createdUtc.getTime()) ? createdUtc : new Date(),
  };
};
